import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { MapPin, ShoppingBag, Receipt, LogOut, Menu, X, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const menuItems = [
  { icon: MapPin, label: 'Lotes', path: '/portal-comisionista' },
  { icon: MapPin, label: 'Mis Apartados', path: '/portal-comisionista/mis-apartados' },
  { icon: ShoppingBag, label: 'Mis Ventas', path: '/portal-comisionista/mis-ventas' },
  { icon: Receipt, label: 'Mis Comisiones', path: '/portal-comisionista/mis-comisiones' },
];

export function ComisionistaLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const [fraccionamientos, setFraccionamientos] = useState([]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!res.ok) { navigate('/login'); return; }
        const data = await res.json();
        const role = data.account_role || data.role;
        if (role !== 'comisionista') { navigate('/dashboard'); return; }
        setUser(data);

        // Load fraccionamientos
        const fRes = await fetch(`${BACKEND_URL}/api/comisionista/mis-fraccionamientos`, { credentials: 'include' });
        if (fRes.ok) {
          const fraccs = await fRes.json();
          setFraccionamientos(fraccs);
          if (fraccs.length === 0) setNoAccess(true);
        }
      } catch { navigate('/login'); }
      finally { setLoading(false); }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      toast.success('Sesión cerrada');
      navigate('/login');
    } catch { toast.error('Error al cerrar sesión'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>;
  }

  if (noAccess) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Sin acceso activo</h2>
          <p className="text-zinc-400 mb-6">No tienes acceso a ningún fraccionamiento activo. Contacta al administrador.</p>
          <Button onClick={handleLogout} variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />Cerrar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-950">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-black border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <img src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png"
          alt="Ibañez" className="h-8" />
        <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-white">
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      {/* Mobile overlay */}
      {mobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileMenuOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed h-full bg-black text-white flex flex-col z-50 transition-transform duration-300 border-r border-zinc-800
        lg:w-64 lg:translate-x-0 w-72 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:top-0 top-0`}>
        {/* Logo */}
        <div className="p-4 border-b border-zinc-800 hidden lg:block">
          <img src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png"
            alt="Ibañez" className="w-3/4 mx-auto" />
        </div>
        <div className="h-14 lg:hidden border-b border-zinc-800 flex items-center px-4">
          <span className="text-[#C6A54A] font-bold">Portal Comisionista</span>
        </div>

        {/* Multi-fraccionamiento info */}
        {fraccionamientos.length > 0 && (
          <div className="mx-3 mt-3 p-3 bg-zinc-900 rounded-lg border border-zinc-800">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Fraccionamientos</p>
            {fraccionamientos.map(f => (
              <div key={f.fraccionamiento_id} className="flex items-center justify-between py-1">
                <span className="text-sm text-zinc-300 truncate">{f.nombre}</span>
                <span className="text-xs text-[#C6A54A] font-bold">{f.porcentaje_comision}%</span>
              </div>
            ))}
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto mt-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive ? 'bg-[#C6A54A] text-black font-semibold' : 'text-zinc-400 hover:bg-zinc-900 hover:text-white'
                }`}>
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <Avatar className="w-10 h-10 border-2 border-[#C6A54A]">
              <AvatarFallback className="bg-[#C6A54A] text-black font-bold">
                {user?.name?.[0]?.toUpperCase() || 'C'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-[#C6A54A] truncate">Comisionista</p>
            </div>
          </div>
          <Button data-testid="portal-logout-btn" onClick={handleLogout} variant="outline" size="sm"
            className="w-full border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 min-h-screen bg-zinc-950">
        {children}
      </main>
    </div>
  );
}

export default ComisionistaLayout;
